// ───────────────────────────────────────────────────────────────
// BulkEditModal – portal overlay chrome (JournalEntryTemplatesModal style)
// • works as bulk‑editor  ➜   ids = [ … ]
// • works as duplicate UI ➜   ids = [ oneId ] + duplicateMode flag
// (dropdown menus follow dir="rtl" now)
// ───────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Portal, IconButton, Tooltip, Box, TextField,
    FormControlLabel, Checkbox,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';

import CustomMonthDatePicker from '../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import Constants from '../../assests/Constants/constants';
import ForeignCurrencyService from '../../Services/foreignCurrency.service';
import ClientService from '../../Services/client.service';
import { toast } from 'react-toastify';
import { calculateVatAmount, calculateGrossFromNet, getVatValue } from '../../Utils/GetVatValue';
import { beautifyNumber } from '../../Utils/FormatNumber';
import ProjectsService from '../../Services/projects.service';
import ProjectModal from '../Projects/ProjectModal.jsx';
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import { lookupSortCodeVatType, resolveSortCodeVatTypeLabel } from '../../Utils/sortCodeDocTypeMismatch';
import fieldStyles from '../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import modalStyles from '../JournalEntryies/SubComponents/JournalEntryTemplatesModal.module.scss';
import shellStyles from './BulkEditModal.module.scss';
import Draggable from 'react-draggable';
const evaluateAmountExpression = (raw) => {
    if (raw == null) return null;
    const str = String(raw).trim().replace(/\s+/g, '');
    if (!str) return null;

    if (/^-?\d+(\.\d+)?$/.test(str)) {
        const n = Number(str);
        return Number.isFinite(n) ? n : null;
    }

    // Only digits, decimal point, and + - * / ( )
    if (!/^[0-9+\-*/().]+$/.test(str)) return null;

    try {
        // eslint-disable-next-line no-new-func
        const result = Function(`"use strict"; return (${str});`)();
        if (typeof result !== 'number' || !Number.isFinite(result)) return null;
        return Math.round(result * 10000) / 10000;
    } catch {
        return null;
    }
};

const EMPTY_DUPLICATE_VALUES = {
    reportingMonthDate: null,
    registrationMonthDate: null,
    documentDate: null,
    creditAccount: '',
    debitAccount: '',
    sortCodeId: '',
    vat: '',
    amount: '',
    foreignCurrencyCode: '',
    foreignCurrencyAmount: '',
    reference: '',
    reference2: '',
    accountCodeId: '',
    description: '',
    withholdingTax: '',
    journalEntryType: 0,
};

function utcMonthDate(dateValue) {
    if (!dateValue) return null;
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), 10));
}

function utcDocumentDateIso(dateValue) {
    if (!dateValue) return null;
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
}

function buildJournalDuplicateSeed(sourceTransaction, sortCodeIdOptions) {
    const seed = {
        ...EMPTY_DUPLICATE_VALUES,
        description: sourceTransaction?.description || '',
        reference: sourceTransaction?.reference || '',
    };

    const vatLabel = resolveSortCodeVatTypeLabel(
        lookupSortCodeVatType(sourceTransaction?.sortCodeId, sortCodeIdOptions, sourceTransaction)
    );

    if (vatLabel === 'VatTransactions') {
        seed.debitAccount = sourceTransaction?.debitAccount || '';
    } else if (vatLabel === 'VatExpenses' || vatLabel === 'VatEquipment') {
        seed.creditAccount = sourceTransaction?.creditAccount || '';
    }

    return seed;
}

export default function BulkEditModal({
    open,
    ids,                         // array of source‑id(s) – may be length 1 in duplicateMode
    sourceTransaction,           // source transaction data (for duplicate mode)
    onClose,
    onSavePatch,                 // async (patch) → parent decides (bulk‑update OR duplicate)
    formattedAccountCardOptions,
    sortCodeIdOptions,
    client,
    duplicateMode = false,       // ← NEW   true = opened from "Duplicate" button
    accountCardOptions,          // ← NEW   needed for sortCodeId logic
    type = 'Expenses',           // ← NEW   document type (Expenses/Incomes)
    token = '',
    titleKey,                    // header translation key in duplicateMode (defaults to DuplicateEntry)
    lockedSelection = false,     // every selected row is locked: only month dates can change
}) {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();            // "rtl" | "ltr"
    const [busy, setBusy] = useState(false);
    const [container] = useState(() => document.createElement('div'));
    const nodeRef = useRef(null);
    const [FCRate, setFCRate] = useState(1);

    // Add local state for editing values (like EditModal)
    const [amountInput, setAmountInput] = useState('');
    const [currencyAmountInput, setCurrencyAmountInput] = useState('');
    const [netAmountInput, setNetAmountInput] = useState('');
    const lastAmountEditRef = useRef('gross');
    const netFocusedRef = useRef(false);
    const netAmountInputRef = useRef('');

    // Foreign-currency warnings/errors like EditModal
    const [currencyWarn, setCurrencyWarn] = useState(false);
    const [currencyError, setCurrencyError] = useState(false);
    const [dateError, setDateError] = useState(false);
    const [reportingMonthDatePickerKey, setReportingMonthDatePickerKey] = useState(0);

    // Saved entries for batch processing
    const [savedEntries, setSavedEntries] = useState([]);
    const [editingEntryIndex, setEditingEntryIndex] = useState(null);

    // Bulk-edit only: flip amount and/or flip debit/credit for the selected ids (not just apply form values)
    const [flipAmountForSelected, setFlipAmountForSelected] = useState(false);
    const [flipCardsForSelected, setFlipCardsForSelected] = useState(false);
    const [alignRegistrationMonthToDocumentDate, setAlignRegistrationMonthToDocumentDate] = useState(false);
    // Bulk-edit only: replace each selected doc's description with the related account card name
    // (Expenses → creditAccount card name, Incomes → debitAccount card name,
    //  JournalEntries → resolved server-side based on sort code VAT type).
    const [replaceDescriptionForSelected, setReplaceDescriptionForSelected] = useState(false);

    // The "replace description with account name" option only applies to Expenses, Incomes and
    // JournalEntries. PaymentAcceptances (and any other type) is neither expense nor income,
    // so the option is hidden there.
    const supportsReplaceDescriptionOption = (
        type === 'Expenses' ||
        type === 'Incomes' ||
        type === 'JournalEntries'
    );

    const showProjectAssignRow =
        !duplicateMode &&
        (type === 'Expenses' || type === 'Incomes' || type === 'JournalEntries') &&
        Boolean(client?.caseId && token);

    const [segmentationProjects, setSegmentationProjects] = useState([]);
    const [projectModalOpen, setProjectModalOpen] = useState(false);
    const [projectCreateLoading, setProjectCreateLoading] = useState(false);
    const [projectAssignTouched, setProjectAssignTouched] = useState(false);
    const [selectedProjectOption, setSelectedProjectOption] = useState(null);

    const projectAutocompleteOptions = useMemo(
        () => [
            { value: '__ADD_NEW__', label: t('AddProject'), kind: 'add' },
            ...segmentationProjects.map((p) => ({
                value: String(p.id ?? ''),
                label: `${p.code} — ${p.name}`,
                kind: 'project',
            })),
        ],
        [segmentationProjects, t]
    );

    const accountSelectOptions = useMemo(
        () =>
            (formattedAccountCardOptions || []).map((o) => ({
                ...o,
                value: o.value ?? o.cardId,
                label: o.label || `${o.cardId} - ${o.name}`,
            })),
        [formattedAccountCardOptions]
    );

    const findAccountOption = useCallback(
        (accountId) => {
            if (accountId == null || accountId === '') return null;
            return (
                accountSelectOptions.find(
                    (o) =>
                        String(o.value) === String(accountId) ||
                        String(o.cardId) === String(accountId)
                ) || null
            );
        },
        [accountSelectOptions]
    );

    const sortCodeSelectOptions = useMemo(
        () =>
            (sortCodeIdOptions || []).map((o) => ({
                value: o.sortCodeId,
                label: `${o.sortCodeId} - ${o.sortCodeName}`,
            })),
        [sortCodeIdOptions]
    );

    const vatSelectOptions = useMemo(
        () => [
            { value: '', label: t('NoChange') },
            ...Constants.VATPercentage.map((v) => ({
                value: v,
                label: v === 16 ? t('AccountingTax') : `${v}%`,
            })),
        ],
        [t]
    );

    const currencySelectOptions = useMemo(
        () => [
            { value: '', label: t('NoChange') },
            { value: '__CLEAR__', label: t('Clear') },
            ...Object.entries(Constants.ForeignCurrencyCode).map(([code, num]) => ({
                value: num,
                label: `${code} ${Constants.ForeignCurrencySymbol[code]}`,
            })),
        ],
        [t]
    );

    const fetchSegmentationProjects = useCallback(async () => {
        if (!client?.caseId || !token) {
            setSegmentationProjects([]);
            return;
        }
        try {
            const res = await ProjectsService.getProjects(client.caseId, token);
            if (!res.ok) return;
            const data = await res.json();
            const list = (Array.isArray(data) ? data : [])
                .map((p) => ({
                    id: String(p.projectId ?? p.id ?? ''),
                    code: p.code ?? '',
                    name: p.name ?? '',
                }))
                .filter((p) => p.id);
            setSegmentationProjects(list);
        } catch (error) {
            console.error('Error fetching projects:', error);
        }
    }, [client?.caseId, token]);

    useEffect(() => {
        if (!open || !showProjectAssignRow) return;
        setProjectAssignTouched(false);
        setSelectedProjectOption(null);
        void fetchSegmentationProjects();
    }, [open, showProjectAssignRow, fetchSegmentationProjects]);

    const handleProjectAutocompleteChange = (_event, newValue) => {
        setProjectAssignTouched(true);
        if (newValue?.kind === 'add' || newValue?.value === '__ADD_NEW__') {
            setProjectModalOpen(true);
            return;
        }
        setSelectedProjectOption(newValue && newValue.kind === 'project' ? newValue : null);
    };

    const handleProjectModalClose = () => {
        setProjectModalOpen(false);
    };

    const handleProjectModalSave = async (projectInput) => {
        if (!client?.caseId || !token) return false;
        const drafts = Array.isArray(projectInput) ? projectInput : [projectInput];
        const normalized = drafts.map((d) => ({
            code: String(d?.code ?? '').trim(),
            name: String(d?.name ?? '').trim(),
        }));
        if (normalized.some((d) => !d.code || !d.name)) return false;

        setProjectCreateLoading(true);
        try {
            let lastCreated = null;
            for (const draft of normalized) {
                const res = await ProjectsService.createProject(
                    { CaseId: client.caseId, Code: draft.code, Name: draft.name },
                    token
                );
                if (!res.ok) {
                    const msg = await res.text();
                    toast.error(msg || t('ErrorCreatingProject'));
                    await fetchSegmentationProjects();
                    return false;
                }
                lastCreated = await res.json();
            }
            toast.success(`${t('Add')} ${t('Projects')}`);
            await fetchSegmentationProjects();
            setProjectModalOpen(false);
            if (lastCreated?.id) {
                setProjectAssignTouched(true);
                setSelectedProjectOption({
                    value: String(lastCreated.id),
                    label: `${lastCreated.code} — ${lastCreated.name}`,
                    kind: 'project',
                });
            }
            return true;
        } catch (e) {
            console.error(e);
            toast.error(t('ErrorCreatingProject'));
            return false;
        } finally {
            setProjectCreateLoading(false);
        }
    };

    /* ------------------------------------------------------------ */
    const [values, setValues] = useState({
        reportingMonthDate: null,
        registrationMonthDate: null,
        documentDate: null, // For VAT calculation
        creditAccount: '',
        debitAccount: '',
        sortCodeId: '',
        vat: '',
        amount: '',
        foreignCurrencyCode: '',
        foreignCurrencyAmount: '',
        reference: '',
        reference2: '',
        accountCodeId: '', // For PaymentAcceptances
        description: '', // For PaymentAcceptances
        withholdingTax: '', // For PaymentAcceptances
        journalEntryType: 0 // For JournalEntries (0 = NormalJournal, 1 = IsDepreciationJournal)
    });

    useEffect(() => {
        if (!open) return;
        document.body.appendChild(container);
        return () => { try { document.body.removeChild(container); } catch { /* ignore */ } };
    }, [open, container]);

    const getDuplicateFormValues = useCallback(() => {
        if (!sourceTransaction || !duplicateMode) return null;

        const isJournalDuplicate =
            type === 'JournalEntries' ||
            type === 'JournalEntry' ||
            sourceTransaction.docType === 'JournalEntries' ||
            sourceTransaction.docType === 'JournalEntry' ||
            sourceTransaction.documentType === 'JournalEntries';

        if (isJournalDuplicate) {
            return buildJournalDuplicateSeed(sourceTransaction, sortCodeIdOptions);
        }

        const reportingMonthDate = utcMonthDate(sourceTransaction.reportingMonthDate);
        const registrationMonthDate = utcMonthDate(sourceTransaction.registrationMonthDate);
        const documentDate = utcDocumentDateIso(sourceTransaction.documentDate);

        let debitAccount = sourceTransaction.debitAccount || '';
        let creditAccount = sourceTransaction.creditAccount || '';

        if (type === 'PaymentAcceptances' && (!debitAccount || !creditAccount)) {
            const itemTypes = ['checks', 'creditCards', 'cash', 'bankTransactions', 'offsets'];
            for (const itemType of itemTypes) {
                const items = sourceTransaction[itemType];
                if (items && items.length > 0 && items[0]) {
                    if (!debitAccount && items[0].debitAccount) {
                        debitAccount = items[0].debitAccount;
                    }
                    if (!creditAccount && items[0].creditAccount) {
                        creditAccount = items[0].creditAccount;
                    }
                    if (debitAccount && creditAccount) break;
                }
            }
        }

        const defaultSortCodeId = (type === 'PaymentAcceptances' && !sourceTransaction.sortCodeId)
            ? '3'
            : (sourceTransaction.sortCodeId || '');
        let defaultVat = (type === 'PaymentAcceptances' && !sourceTransaction.vat)
            ? 100
            : (sourceTransaction.vat ?? ''); // ?? preserves an explicit 0% VAT (0 || '' would drop it)

        let sortCodeDebit = debitAccount;
        let sortCodeCredit = creditAccount;
        if (defaultSortCodeId && sortCodeIdOptions?.length > 0) {
            const sortCode = sortCodeIdOptions.find(sc => sc.sortCodeId === defaultSortCodeId);
            if (sortCode) {
                defaultVat = sortCode.vat ?? defaultVat;

                if (defaultSortCodeId === '1') {
                    sortCodeCredit = '';
                    sortCodeDebit = sortCode.debitAccount || debitAccount;
                } else if (defaultSortCodeId === '2') {
                    sortCodeDebit = '';
                    sortCodeCredit = sortCode.creditAccount || creditAccount;
                } else {
                    sortCodeCredit = creditAccount || sortCode.creditAccount || '';
                    sortCodeDebit = debitAccount || sortCode.debitAccount || '';
                }
            }
        }

        const vatLabel = resolveSortCodeVatTypeLabel(
            lookupSortCodeVatType(defaultSortCodeId, sortCodeIdOptions, sourceTransaction)
        );
        const isIncomeDuplicate =
            type === 'Incomes' ||
            type === 'Income' ||
            sourceTransaction.docType === 'Incomes' ||
            sourceTransaction.documentType === 'Incomes' ||
            vatLabel === 'VatTransactions';
        const isExpenseDuplicate =
            type === 'Expenses' ||
            type === 'Expense' ||
            sourceTransaction.docType === 'Expenses' ||
            sourceTransaction.documentType === 'Expenses' ||
            vatLabel === 'VatExpenses' ||
            vatLabel === 'VatEquipment';

        if (isIncomeDuplicate) {
            sortCodeCredit = '';
            sortCodeDebit = debitAccount || sortCodeDebit;
        } else if (isExpenseDuplicate) {
            sortCodeDebit = '';
            sortCodeCredit = creditAccount || sortCodeCredit;
        }

        return {
            ...EMPTY_DUPLICATE_VALUES,
            reportingMonthDate: reportingMonthDate ? reportingMonthDate.toISOString() : null,
            registrationMonthDate: registrationMonthDate ? registrationMonthDate.toISOString() : null,
            documentDate,
            creditAccount: sortCodeCredit,
            debitAccount: sortCodeDebit,
            sortCodeId: defaultSortCodeId,
            vat: defaultVat,
            amount: '',
            foreignCurrencyCode: sourceTransaction.foreignCurrencyCode || '',
            foreignCurrencyAmount: '',
            reference: sourceTransaction.reference || '',
            reference2: sourceTransaction.reference2 || '',
            accountCodeId: sourceTransaction.accountCodeId || '',
            description: sourceTransaction.description || '',
            withholdingTax: sourceTransaction.withholdingTax || '',
            journalEntryType: sourceTransaction.journalEntryType ?? sourceTransaction.JournalEntryType ?? 0,
        };
    }, [duplicateMode, sourceTransaction, sortCodeIdOptions, type]);

    // Auto-populate from source transaction in duplicate mode.
    // Journal entries stay blank except description, reference, and the
    // expense credit account / income debit account.
    useEffect(() => {
        if (!duplicateMode || !sourceTransaction) return;

        const seed = getDuplicateFormValues();
        if (!seed) return;

        setValues(prev => ({ ...prev, ...seed }));
        setAmountInput(seed.amount || '');
        setCurrencyAmountInput(seed.foreignCurrencyAmount || '');
        setNetAmountInput('');
        lastAmountEditRef.current = 'gross';
    }, [duplicateMode, sourceTransaction, getDuplicateFormValues]);

    // Foreign currency rate fetching
    useEffect(() => {
        async function fetchRate() {
            try {
                if (values.foreignCurrencyCode !== null && values.foreignCurrencyCode !== '' && values.foreignCurrencyCode !== undefined && values.reportingMonthDate) {
                    const rate = await ForeignCurrencyService.GetIlsRateForDate(
                        Number(values.foreignCurrencyCode),
                        new Date(values.reportingMonthDate)
                    );
                    setFCRate(rate);
                }
            } catch (err) {
                console.error(err);
                toast.error(t('CurrencyRateFetchError'));
            }
        }

        if (values.foreignCurrencyCode !== null && values.foreignCurrencyCode !== '' && values.foreignCurrencyCode !== undefined && values.reportingMonthDate) {
            fetchRate();
        }
    }, [values.foreignCurrencyCode, values.reportingMonthDate, t]);

    // Sync local state with values when they change externally (like EditModal)
    useEffect(() => {
        setAmountInput(values.amount ?? '');
    }, [values.amount]);

    useEffect(() => {
        setCurrencyAmountInput(values.foreignCurrencyAmount ?? '');
    }, [values.foreignCurrencyAmount]);

    // Currency warning validation (same as EditModal's logic, adapted to values)
    useEffect(() => {
        const { foreignCurrencyCode, reportingMonthDate, foreignCurrencyAmount } = values;
        setCurrencyError(foreignCurrencyCode === null || foreignCurrencyCode === '' || foreignCurrencyCode === undefined);
        
        // Only show date error if date is invalid (not if it's empty)
        if (reportingMonthDate) {
            const dateObj = new Date(reportingMonthDate);
            setDateError(isNaN(dateObj.getTime()));
        } else {
            setDateError(false);
        }
        
        if (
            (foreignCurrencyCode == null || reportingMonthDate == null || foreignCurrencyAmount == null) &&
            values.foreignCurrencyAmount
        ) {
            setCurrencyWarn(true);
        } else {
            setCurrencyWarn(false);
        }
    }, [values.foreignCurrencyCode, values.reportingMonthDate, values.foreignCurrencyAmount]);

    // VAT calculation using useMemo (recalculates immediately when dependencies change)
    // Uses documentDate instead of reportingMonthDate for VAT calculation
    // If documentDate is null, defaults to 18% VAT (uses date >= 2025-01-01)
    // A VAT of 0 is a valid, explicitly-set value — treat it as "set" (not falsy/unset).
    const hasVatValue = values.vat !== '' && values.vat != null;
    const currentVat = useMemo(() => {
        if (hasVatValue && values.amount && Number(values.vat) !== 0) {
            // Use documentDate if available, otherwise default to a date that results in 18% VAT
            const dateForVat = values.documentDate 
                ? new Date(values.documentDate) 
                : new Date('2025-01-01'); // Default to 18% VAT
            return getVatValue(dateForVat, false, Number(values.vat) === 16 || Number(values.vat) === 17);
        }
        return 0;
    }, [hasVatValue, values.vat, values.amount, values.documentDate]);

    const beforeVatAmount = useMemo(() => {
        if (hasVatValue && values.amount) {
            // Use documentDate if available, otherwise default to a date that results in 18% VAT
            const dateForVat = values.documentDate 
                ? new Date(values.documentDate) 
                : new Date('2025-01-01'); // Default to 18% VAT
            const amount = Number(values.amount);
            const vatAmount = calculateVatAmount(amount, values.vat, dateForVat);
            return amount - vatAmount;
        }
        return 0;
    }, [hasVatValue, values.vat, values.amount, values.documentDate]);

    useEffect(() => {
        netAmountInputRef.current = netAmountInput;
    }, [netAmountInput]);

    useEffect(() => {
        lastAmountEditRef.current = 'gross';
        netFocusedRef.current = false;
    }, [sourceTransaction, duplicateMode, open]);

    useEffect(() => {
        if (lastAmountEditRef.current === 'net') return;
        if (values.amount === '' || values.amount == null) {
            if (!netFocusedRef.current) setNetAmountInput('');
            return;
        }
        if (!netFocusedRef.current) {
            setNetAmountInput(String(Number(Number(beforeVatAmount).toFixed(2))));
        }
    }, [beforeVatAmount, values.amount]);

    useEffect(() => {
        if (lastAmountEditRef.current !== 'net') return;
        const net = Number(netAmountInputRef.current);
        if (!Number.isFinite(net) || netAmountInputRef.current === '') return;
        const dateForVat = values.documentDate || '2025-01-01';
        const gross = Number(calculateGrossFromNet(net, values.vat || 0, dateForVat).toFixed(2));
        const current = Number(values.amount);
        if (Number.isFinite(current) && Math.abs(current - gross) < 0.001) return;
        setValues((prev) => ({ ...prev, amount: gross }));
        setAmountInput(String(gross));
    }, [values.vat, values.documentDate]);

    /* field chrome (AddJournalEntryModal / TemplatesModal) — must stay above any early return */
    const comboInputProps = useMemo(() => ({ disableUnderline: true }), []);
    const inputStyle = useMemo(
        () => ({ style: { textAlign: dir === 'ltr' ? 'left' : 'right' } }),
        [dir]
    );
    const lockOtherFields = !duplicateMode && lockedSelection;
    const lockedFieldStyle = lockOtherFields ? { opacity: 0.55, pointerEvents: 'none' } : undefined;
    // Locked incomes / expenses / journal entries may still change VAT.
    const vatEditableWhenLocked = ['Incomes', 'Expenses', 'JournalEntries'].includes(type);

    if (!open) return null;

    const handleField = (k, v) => {
        const updatedValue = v;

        if (k === 'sortCodeId') {
            if (!v || !sortCodeIdOptions?.some((option) => option.sortCodeId === v)) {
                // Set default values for the empty state
                setValues({
                    ...values,
                    vat: 100, // Default VAT
                    [k]: '',
                });
                return; // Exit early since there's no valid sortCodeId
            }

            const name = sortCodeIdOptions.find(
                (option) => option.sortCodeId === v
            )?.sortCodeName || ''

            // Extract the name without the parentheses
            const nameWithoutParentheses = name.replace(/\s*\(.*?\)\s*/, '').trim();

            // Check for a number within parentheses
            const match = name.match(/\((\d+)%?/);
            let customVat = match ? parseInt(match[1], 10) : null;

            const sortCode = sortCodeIdOptions.find((sc) => sc.sortCodeId === v);
            const newVat = sortCode?.vat ?? 100;

            const accountCard = accountCardOptions?.find(
                (item) => item.cardId === (type === 'Incomes' ? sortCode.debitAccount : sortCode.creditAccount)
            );

            const debit = accountCardOptions?.find(
                (item) => item.cardId === sortCode.debitAccount
            );

            const credit = accountCardOptions?.find(
                (item) => item.cardId === sortCode.creditAccount
            );

            if (v === '1') { //hova
                setValues({
                    ...values,
                    vat: newVat,
                    [k]: updatedValue,
                    creditAccount: '',
                    debitAccount: debit ? debit.cardId : values?.debitAccount,
                });
                return;
            }
            if (v === '2') { //z5ot
                setValues({
                    ...values,
                    vat: newVat,
                    [k]: updatedValue,
                    creditAccount: credit ? credit.cardId : values?.creditAccount,
                    debitAccount: '',
                });
                return;
            }

            // Automatically set JournalEntryType to IsDepreciationJournal (1) for sort code 905
            // (matching logic from JournalEntryDraggableDialog)
            const updatedValues = {
                ...values,
                vat: newVat,
                [k]: updatedValue,
                creditAccount: credit ? credit.cardId : values?.creditAccount,
                debitAccount: debit ? debit.cardId : values?.debitAccount,
            };

            // Set journalEntryType = 1 for sort code 905 (depreciation journal)
            if (v === '905' && type === 'JournalEntries') {
                updatedValues.journalEntryType = 1; // IsDepreciationJournal
            }

            setValues(updatedValues);
        } else {
            // For fields other than sortCodeId (or for PaymentAcceptances), just update the values
            setValues({
                ...values,
                [k]: updatedValue,
            });
        }
    };

    const handleReportingMonthDateChange = async (date) => {
        if (!date) return;

        // Journal entries may be posted into a closed month (unlike incomes/expenses)
        const isJournalEntry =
            type === 'JournalEntries' ||
            type === 'JournalEntry' ||
            sourceTransaction?.docType === 'JournalEntries' ||
            sourceTransaction?.docType === 'JournalEntry';

        if (!isJournalEntry && !lockedSelection && client?.caseId && token) {
            const newDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 10));
            const response = await ClientService.CheckIfMonthClosed(client.caseId, newDate, token);
            if (response.ok) {
                const isClosedReportingMonth = await response.json();
                if (isClosedReportingMonth) {
                    toast.info(t('MonthReportAlreadyClosedTryAgain'));
                    setReportingMonthDatePickerKey((prev) => prev + 1);
                    return;
                }
            }
        }

        handleField('reportingMonthDate', date.toISOString());
    };

    // Handlers for foreign currency conversion (exactly like EditModal)
    const handleChangeAmount = (value) => {
        handleField('amount', value);
        if (
            values.foreignCurrencyCode !== null &&
            values.foreignCurrencyCode !== '' &&
            values.foreignCurrencyCode !== undefined &&
            values.reportingMonthDate
        ) {
            handleField('foreignCurrencyAmount', value / FCRate);
        }
    };

    const handleChangeNetAmount = (value) => {
        lastAmountEditRef.current = 'net';
        setNetAmountInput(value);
        if (value === '' || isNaN(Number(value))) {
            handleChangeAmount('');
            return;
        }
        const dateForVat = values.documentDate || '2025-01-01';
        const gross = Number(calculateGrossFromNet(Number(value), values.vat || 0, dateForVat).toFixed(2));
        handleChangeAmount(String(gross));
    };

    const handleChangeForeignCurrencyAmount = (value) => {
        if (
            values.foreignCurrencyCode === null ||
            values.foreignCurrencyCode === '' ||
            values.foreignCurrencyCode === undefined
        ) {
            toast.error(t('PleaseSelectCurrencyCodeFirst'));
            return;
        }
        handleField('foreignCurrencyAmount', value);
        if (
            values.foreignCurrencyCode !== null &&
            values.foreignCurrencyCode !== '' &&
            values.foreignCurrencyCode !== undefined &&
            values.reportingMonthDate
        ) {
            handleField('amount', value * FCRate);
        }
    };

    const handleSwapAccounts = () => {
        if (busy) return;
        
        // Swap the debitAccount and creditAccount values atomically
        const tempDebit = values.debitAccount;
        const tempCredit = values.creditAccount;
        
        setValues(prev => ({
            ...prev,
            debitAccount: tempCredit,
            creditAccount: tempDebit
        }));
    };

    // Flip amount sign: positive → negative, negative → positive (and foreign currency amount if set)
    const handleFlipAmount = () => {
        if (busy) return;
        const raw = values.amount;
        if (raw === '' || raw === null || raw === undefined) {
            toast.error(t('PleaseEnterAmount'));
            return;
        }
        const num = Number(raw);
        if (isNaN(num)) return;
        const newAmount = (-num).toFixed(2);
        lastAmountEditRef.current = 'gross';
        let newFc = values.foreignCurrencyAmount;
        if (
            values.foreignCurrencyCode != null &&
            values.foreignCurrencyCode !== '' &&
            values.foreignCurrencyAmount != null &&
            values.foreignCurrencyAmount !== ''
        ) {
            const fcNum = Number(values.foreignCurrencyAmount);
            if (!isNaN(fcNum)) newFc = (-fcNum).toFixed(2);
        }
        setValues(prev => ({ ...prev, amount: newAmount, foreignCurrencyAmount: newFc }));
        setAmountInput(newAmount);
        if (newFc !== values.foreignCurrencyAmount) setCurrencyAmountInput(String(newFc));
    };

    const convertSourceTransactionToValues = () => getDuplicateFormValues();

    // Add current entry to saved entries list
    const handleAddToList = () => {
        // Always use current form values (user's changes)
        const processedValues = { ...values };

        // Validate required fields
        if (!processedValues.reportingMonthDate) {
            toast.error(t('PleaseSelectReportingMonth'));
            return;
        }
        if (!processedValues.amount || processedValues.amount === '') {
            toast.error(t('PleaseEnterAmount'));
            return;
        }

        // Build the entry patch
        const entry = buildEntryPatch(processedValues);
        
        if (editingEntryIndex !== null) {
            // Update existing entry
            const updatedEntries = [...savedEntries];
            updatedEntries[editingEntryIndex] = entry;
            setSavedEntries(updatedEntries);
            setEditingEntryIndex(null);
            toast.success(t('EntryUpdated'));
        } else {
            // Add new entry
            setSavedEntries([...savedEntries, entry]);
            toast.success(t('EntryAddedToList'));
        }

        // Reset form for next entry (back to original source data in duplicate mode)
        lastAmountEditRef.current = 'gross';
        netFocusedRef.current = false;
        if (duplicateMode && sourceTransaction) {
            const sourceValues = convertSourceTransactionToValues();
            if (sourceValues) {
                setValues(sourceValues);
                setAmountInput(sourceValues.amount || '');
                setCurrencyAmountInput(sourceValues.foreignCurrencyAmount || '');
                setNetAmountInput('');
            } else {
                resetForm();
            }
        } else {
            resetForm();
        }
    };

    // Build entry patch from values
    const buildEntryPatch = (processedValues) => {
        const entry = { ...processedValues };

        // Translate the currency code tri-state
        if (entry.foreignCurrencyCode === "__CLEAR__") {
            entry.foreignCurrencyCode = "";
            entry.foreignCurrencyAmount = "";
        }

        // Cast numbers when user actually set values
        if (entry.foreignCurrencyCode !== "" && entry.foreignCurrencyCode !== null) {
            entry.foreignCurrencyCode = Number(entry.foreignCurrencyCode);
        }
        if (entry.foreignCurrencyAmount !== "" && entry.foreignCurrencyAmount !== null) {
            entry.foreignCurrencyAmount = Number(entry.foreignCurrencyAmount);
        }
        if (entry.amount !== "" && entry.amount !== null) {
            entry.amount = Number(entry.amount);
        }
        if (entry.withholdingTax !== "" && entry.withholdingTax !== null) {
            entry.withholdingTax = Number(entry.withholdingTax);
        }
        if (entry.journalEntryType !== "" && entry.journalEntryType !== null && entry.journalEntryType !== undefined) {
            entry.journalEntryType = Number(entry.journalEntryType);
        }

        // Filter out fields that don't apply to this document type
        // For duplicate mode (when adding to list), include all fields even if empty strings
        const filtered = Object.fromEntries(
            Object.entries(entry).filter(([k, v]) => {
                if (v === null) return false;
                
                // Include empty strings for string fields in duplicate mode
                if (v === "") {
                    const stringFields = ['reference', 'description', 'accountCodeId', 'debitAccount', 'creditAccount', 
                                         'foreignCurrencyCode', 'foreignCurrencyAmount', 'withholdingTax'];
                    if (stringFields.includes(k)) {
                        return true;
                    }
                    // For other fields, only include if they're explicitly clearable
                    return k === "foreignCurrencyCode" || k === "foreignCurrencyAmount";
                }
                
                if (type === 'PaymentAcceptances') {
                    if (k === 'reference2') return false;
                } else {
                    if (k === 'accountCodeId' || k === 'withholdingTax') {
                        return false;
                    }
                }
                
                if (k === 'journalEntryType' && type === 'JournalEntries') {
                    return true;
                }
                
                return true;
            })
        );

        return filtered;
    };

    // Reset form to initial state
    const resetForm = () => {
        setValues({
            reportingMonthDate: null,
            registrationMonthDate: null,
            documentDate: null,
            creditAccount: '',
            debitAccount: '',
            sortCodeId: '',
            vat: '',
            amount: '',
            foreignCurrencyCode: '',
            foreignCurrencyAmount: '',
            reference: '',
            reference2: '',
            accountCodeId: '',
            description: '',
            withholdingTax: '',
            journalEntryType: 0
        });
        setAmountInput('');
        setCurrencyAmountInput('');
        setEditingEntryIndex(null);
    };

    // Handle row click from saved entries table
    const handleSavedEntryRowClick = (entry, index) => {
        // Load entry data into form for editing
        // Convert entry data back to form format (handle dates, numbers, etc.)
        const formValues = {
            reportingMonthDate: entry.reportingMonthDate || null,
            registrationMonthDate: entry.registrationMonthDate || null,
            documentDate: entry.documentDate || null,
            creditAccount: entry.creditAccount || '',
            debitAccount: entry.debitAccount || '',
            sortCodeId: entry.sortCodeId || '',
            vat: entry.vat ?? '', // ?? preserves an explicit 0% VAT
            amount: entry.amount || '',
            foreignCurrencyCode: entry.foreignCurrencyCode || '',
            foreignCurrencyAmount: entry.foreignCurrencyAmount || '',
            reference: entry.reference || '',
            reference2: entry.reference2 || '',
            accountCodeId: entry.accountCodeId || '',
            description: entry.description || '',
            withholdingTax: entry.withholdingTax || '',
            journalEntryType: entry.journalEntryType ?? 0
        };
        
        // Ensure dates are in ISO string format if they exist
        if (formValues.reportingMonthDate) {
            if (typeof formValues.reportingMonthDate === 'string') {
                // Already in string format, ensure it's valid
                const date = new Date(formValues.reportingMonthDate);
                if (!isNaN(date.getTime())) {
                    formValues.reportingMonthDate = date.toISOString();
                } else {
                    formValues.reportingMonthDate = null;
                }
            } else {
                formValues.reportingMonthDate = new Date(formValues.reportingMonthDate).toISOString();
            }
        }
        
        if (formValues.registrationMonthDate) {
            if (typeof formValues.registrationMonthDate === 'string') {
                const date = new Date(formValues.registrationMonthDate);
                if (!isNaN(date.getTime())) {
                    formValues.registrationMonthDate = date.toISOString();
                } else {
                    formValues.registrationMonthDate = null;
                }
            } else {
                formValues.registrationMonthDate = new Date(formValues.registrationMonthDate).toISOString();
            }
        }
        
        if (formValues.documentDate) {
            const d = new Date(formValues.documentDate);
            if (!isNaN(d.getTime())) {
                formValues.documentDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
            } else {
                formValues.documentDate = null;
            }
        }
        
        // Convert numbers to strings for form fields (except journalEntryType which stays as number)
        if (formValues.amount !== null && formValues.amount !== undefined && formValues.amount !== '') {
            formValues.amount = typeof formValues.amount === 'number' ? String(formValues.amount) : formValues.amount;
        } else {
            formValues.amount = '';
        }
        
        if (formValues.foreignCurrencyAmount !== null && formValues.foreignCurrencyAmount !== undefined && formValues.foreignCurrencyAmount !== '') {
            formValues.foreignCurrencyAmount = typeof formValues.foreignCurrencyAmount === 'number' ? String(formValues.foreignCurrencyAmount) : formValues.foreignCurrencyAmount;
        } else {
            formValues.foreignCurrencyAmount = '';
        }
        
        if (formValues.foreignCurrencyCode !== null && formValues.foreignCurrencyCode !== undefined && formValues.foreignCurrencyCode !== '') {
            formValues.foreignCurrencyCode = typeof formValues.foreignCurrencyCode === 'number' ? String(formValues.foreignCurrencyCode) : formValues.foreignCurrencyCode;
        } else {
            formValues.foreignCurrencyCode = '';
        }
        
        if (formValues.vat !== null && formValues.vat !== undefined && formValues.vat !== '') {
            formValues.vat = typeof formValues.vat === 'number' ? String(formValues.vat) : formValues.vat;
        } else {
            formValues.vat = '';
        }
        
        if (formValues.withholdingTax !== null && formValues.withholdingTax !== undefined && formValues.withholdingTax !== '') {
            formValues.withholdingTax = typeof formValues.withholdingTax === 'number' ? String(formValues.withholdingTax) : formValues.withholdingTax;
        } else {
            formValues.withholdingTax = '';
        }
        
        // journalEntryType stays as number
        if (formValues.journalEntryType === null || formValues.journalEntryType === undefined) {
            formValues.journalEntryType = 0;
        }
        
        // Set form values
        setValues(formValues);
        
        // Set input states for amount and currency amount (formatted to 2 decimals)
        const amountValue = formValues.amount && formValues.amount !== '' 
            ? (typeof formValues.amount === 'number' ? formValues.amount.toFixed(2) : (isNaN(Number(formValues.amount)) ? formValues.amount : Number(formValues.amount).toFixed(2)))
            : '';
        const currencyAmountValue = formValues.foreignCurrencyAmount && formValues.foreignCurrencyAmount !== ''
            ? (typeof formValues.foreignCurrencyAmount === 'number' ? formValues.foreignCurrencyAmount.toFixed(2) : (isNaN(Number(formValues.foreignCurrencyAmount)) ? formValues.foreignCurrencyAmount : Number(formValues.foreignCurrencyAmount).toFixed(2)))
            : '';
        
        setAmountInput(amountValue);
        setCurrencyAmountInput(currencyAmountValue);
        
        // Mark this entry as being edited
        setEditingEntryIndex(index);
        
        toast.info(t('EditingEntry', { number: index + 1 }));
    };

    // Delete entry from saved list
    const handleDeleteSavedEntry = (index) => {
        const updatedEntries = savedEntries.filter((_, i) => i !== index);
        setSavedEntries(updatedEntries);
        if (editingEntryIndex === index) {
            setEditingEntryIndex(null);
        }
    };

    const handleSave = async () => {
        // If there are saved entries (duplicate mode: list + current form; or batch from list only)
        if (savedEntries.length > 0) {
            setBusy(true);
            try {
                let entriesToSave = savedEntries;
                // In duplicate mode: also add current form as the last entry so we create (list length + 1) entries
                if (duplicateMode) {
                    const processedValues = { ...values };
                    if (processedValues.foreignCurrencyCode === "__CLEAR__") {
                        processedValues.foreignCurrencyCode = "";
                        processedValues.foreignCurrencyAmount = "";
                    }
                    if (processedValues.foreignCurrencyCode !== "" && processedValues.foreignCurrencyCode != null) {
                        processedValues.foreignCurrencyCode = Number(processedValues.foreignCurrencyCode);
                    }
                    if (processedValues.foreignCurrencyAmount !== "" && processedValues.foreignCurrencyAmount != null) {
                        processedValues.foreignCurrencyAmount = Number(processedValues.foreignCurrencyAmount);
                    }
                    if (processedValues.amount !== "" && processedValues.amount != null) {
                        processedValues.amount = Number(processedValues.amount);
                    }
                    if (processedValues.withholdingTax !== "" && processedValues.withholdingTax != null) {
                        processedValues.withholdingTax = Number(processedValues.withholdingTax);
                    }
                    if (processedValues.journalEntryType !== "" && processedValues.journalEntryType != null && processedValues.journalEntryType !== undefined) {
                        processedValues.journalEntryType = Number(processedValues.journalEntryType);
                    }
                    const currentFormEntry = buildEntryPatch(processedValues);
                    entriesToSave = [...savedEntries, currentFormEntry];
                }
                await onSavePatch(entriesToSave);
                toast.success(t('AllEntriesSaved', { count: entriesToSave.length }));
                setSavedEntries([]);
                setBusy(false);
                onClose();
            } catch (error) {
                setBusy(false);
                toast.error(t('SaveFailed'));
            }
            return;
        }

        // Otherwise, use the original single-entry save logic
        const processedValues = { ...values };

        // Translate the currency code tri-state
        if (processedValues.foreignCurrencyCode === "__CLEAR__") {
            processedValues.foreignCurrencyCode = "";
            processedValues.foreignCurrencyAmount = "";
        }

        // Cast numbers when user actually set values
        if (processedValues.foreignCurrencyCode !== "" && processedValues.foreignCurrencyCode !== null) {
            processedValues.foreignCurrencyCode = Number(processedValues.foreignCurrencyCode);
        }
        if (processedValues.foreignCurrencyAmount !== "" && processedValues.foreignCurrencyAmount !== null) {
            processedValues.foreignCurrencyAmount = Number(processedValues.foreignCurrencyAmount);
        }
        if (processedValues.amount !== "" && processedValues.amount !== null) {
            processedValues.amount = Number(processedValues.amount);
        }
        if (processedValues.withholdingTax !== "" && processedValues.withholdingTax !== null) {
            processedValues.withholdingTax = Number(processedValues.withholdingTax);
        }
        if (processedValues.journalEntryType !== "" && processedValues.journalEntryType !== null && processedValues.journalEntryType !== undefined) {
            processedValues.journalEntryType = Number(processedValues.journalEntryType);
        }

        // Build patch: for duplicate mode, include all fields (even empty strings for string fields)
        // For bulk-edit mode, only include non-empty values
        const patch = Object.fromEntries(
            Object.entries(processedValues).filter(([k, v]) => {
                if (v === null) return false;               // null = untouched
                
                // For PaymentAcceptances, always include debitAccount and creditAccount if they exist (even if empty strings)
                // This ensures swapped accounts are included in the patch
                if (type === 'PaymentAcceptances' && (k === 'debitAccount' || k === 'creditAccount')) {
                    // Include if it's not null (can be empty string)
                    return v !== null;
                }
                
                // For duplicate mode: include all fields, even empty strings (user can clear fields)
                if (duplicateMode) {
                    if (v === "") {
                        // Include empty strings for string fields in duplicate mode
                        const stringFields = ['reference', 'description', 'accountCodeId', 'debitAccount', 'creditAccount', 
                                             'foreignCurrencyCode', 'foreignCurrencyAmount', 'withholdingTax'];
                        if (stringFields.includes(k)) {
                            return true;
                        }
                        // For other fields, only include if they're explicitly clearable
                        return k === "foreignCurrencyCode" || k === "foreignCurrencyAmount";
                    }
                } else {
                    // For bulk-edit mode: only keep empty string for fields we explicitly want to clear
                    if (v === "") {
                        return k === "foreignCurrencyCode" || k === "foreignCurrencyAmount";
                    }
                }
                
                if (type === 'PaymentAcceptances') {
                    if (k === 'reference2') return false;
                } else {
                    if (k === 'accountCodeId' || k === 'withholdingTax') {
                        return false;
                    }
                }
                
                if (k === 'journalEntryType' && type === 'JournalEntries') {
                    return true;
                }
                
                return true;
            })
        );

        // ①  duplicateMode – always call, even if empty (duplicate "as‑is")
        // ②  bulk‑edit     – skip if user changed nothing and no flip / description options
        const hasFlipOptions = flipAmountForSelected || flipCardsForSelected;
        const hasReplaceDescriptionOption = supportsReplaceDescriptionOption && replaceDescriptionForSelected;
        const hasProjectSync = showProjectAssignRow && projectAssignTouched;
        const alignRegistrationMonth = !duplicateMode && alignRegistrationMonthToDocumentDate;
        if (!duplicateMode && !Object.keys(patch).length && !hasFlipOptions && !hasReplaceDescriptionOption && !hasProjectSync && !alignRegistrationMonth) { onClose(); return; }

        const registrationMonthFromForm = alignRegistrationMonth ? null : patch.registrationMonthDate;
        const patchToSave = (!duplicateMode && lockedSelection)
            ? {
                ...(patch.reportingMonthDate ? { reportingMonthDate: patch.reportingMonthDate } : {}),
                ...(registrationMonthFromForm ? { registrationMonthDate: registrationMonthFromForm } : {}),
                ...(vatEditableWhenLocked && patch.vat !== undefined ? { vat: patch.vat } : {}),
            }
            : Object.fromEntries(
                Object.entries(patch).filter(([key]) => !(alignRegistrationMonth && key === 'registrationMonthDate'))
            );

        if (!duplicateMode && lockedSelection && !Object.keys(patchToSave).length && !alignRegistrationMonth) { onClose(); return; }

        // Debug: Log patch to verify debitAccount and creditAccount are included
        if (type === 'PaymentAcceptances' && duplicateMode) {
            console.log('BulkEditModal - Patch before save:', patch);
            console.log('BulkEditModal - debitAccount:', patch.debitAccount, 'creditAccount:', patch.creditAccount);
        }

        setBusy(true);
        if (duplicateMode) {
            // Single duplicate: save current form as one entry (no list)
            await onSavePatch(patch);
        } else {
            // Pass patch plus flip / description flags so backend can apply per-doc changes for each selected id
            await onSavePatch({
                ...patchToSave,
                alignRegistrationMonthToDocumentDate: alignRegistrationMonth,
                ...(lockedSelection ? {} : {
                    flipAmount: flipAmountForSelected,
                    flipCards: flipCardsForSelected,
                    replaceDescriptionWithAccountName: hasReplaceDescriptionOption,
                    syncProject: hasProjectSync,
                    projectId: hasProjectSync
                        ? (selectedProjectOption?.value ? String(selectedProjectOption.value) : null)
                        : undefined,
                }),
            });
        }
        setBusy(false);
        onClose();
    };

    const handleAmountBlurOrEnter = (raw) => {
        const trimmed = String(raw ?? '').trim();
        if (!trimmed) {
            setAmountInput('');
            handleChangeAmount('');
            lastAmountEditRef.current = 'gross';
            return;
        }
        const evaluated = evaluateAmountExpression(raw);
        if (evaluated != null) {
            const formatted = Number(evaluated).toFixed(2);
            setAmountInput(formatted);
            handleChangeAmount(formatted);
            lastAmountEditRef.current = 'gross';
        }
    };

    const handleCurrencyAmountBlurOrEnter = (raw) => {
        const trimmed = String(raw ?? '').trim();
        if (!trimmed) {
            setCurrencyAmountInput('');
            handleChangeForeignCurrencyAmount('');
            return;
        }
        const evaluated = evaluateAmountExpression(raw);
        if (evaluated != null) {
            const formatted = Number(evaluated).toFixed(2);
            setCurrencyAmountInput(formatted);
            handleChangeForeignCurrencyAmount(formatted);
        }
    };

    const handleNetAmountBlurOrEnter = (raw) => {
        netFocusedRef.current = false;
        const trimmed = String(raw ?? '').trim();
        if (!trimmed) return;
        const evaluated = evaluateAmountExpression(raw);
        if (evaluated != null) {
            const formatted = Number(evaluated).toFixed(2);
            setNetAmountInput(formatted);
            handleChangeNetAmount(formatted);
        }
    };

    // Helper function to get sort code name
    const getSortCodeName = (sortCodeId) => {
        if (!sortCodeId) return '-';
        const sortCode = sortCodeIdOptions?.find(option => option.sortCodeId === sortCodeId);
        return sortCode ? `${sortCode.sortCodeId} - ${sortCode.sortCodeName}` : sortCodeId;
    };

    // Helper function to get account card name
    const getAccountCardName = (accountId) => {
        if (!accountId) return '-';
        const accountCard = accountCardOptions?.find(option => option.cardId === accountId);
        return accountCard ? accountCard.name : accountId;
    };

    /* ------------------------------------------------------------ */
    return (
        <Portal container={container}>
            <div
                className={`${shellStyles.overlay} ${dir === 'rtl' ? shellStyles.rtl : shellStyles.ltr}`}
                onClick={onClose}
            >
                <Draggable
                    handle=".bulk-edit-drag-handle"
                    cancel="button, .MuiIconButton-root, input, textarea, [class*='trigger']"
                    nodeRef={nodeRef}
                >
                    <div
                        ref={nodeRef}
                        className={`${shellStyles.dragShell}${savedEntries.length > 0 ? ` ${shellStyles.dragShellWide}` : ''}`}
                        dir={dir}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className={`${shellStyles.modal}${savedEntries.length > 0 ? ` ${shellStyles.modalWide}` : ''}`}>
                            <div className={modalStyles.body}>
                                <div className={`${modalStyles.header} bulk-edit-drag-handle ${shellStyles.dragHandle}`}>
                                    <Tooltip title={t('Cancel')}>
                                        <IconButton onClick={onClose} className={modalStyles.closeIcon}>
                                            <CloseIcon className={modalStyles.closeIcon} />
                                        </IconButton>
                                    </Tooltip>

                                    <div>
                                        {duplicateMode
                                            ? t(titleKey || 'DuplicateEntry')
                                            : t('BulkEdit') + ` (${ids.length}) ` + t('Selected')}
                                    </div>

                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        {duplicateMode && (
                                            <Tooltip title={t('AddToListForMultipleDuplicates') || 'Add this entry to the list. Save will create all entries in the list plus the current form.'}>
                                                <button
                                                    type="button"
                                                    className={modalStyles.ghostBtn}
                                                    onClick={handleAddToList}
                                                    disabled={busy}
                                                >
                                                    {t('AddToList') || 'Add to List'}
                                                </button>
                                            </Tooltip>
                                        )}
                                        <button
                                            type="button"
                                            className={modalStyles.primaryBtn}
                                            onClick={handleSave}
                                            disabled={busy}
                                        >
                                            {savedEntries.length > 0 ? t('SaveAll') : t('Save')}
                                        </button>
                                    </div>
                                </div>

                                <div className={modalStyles.scroll}>

                    {/* Bulk-edit only: flip options for selected ids */}
                    {!duplicateMode && ids.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, p: 1, borderRadius: 1, border: '1px solid #e5e7f6', bgcolor: 'grey.50' }}>
                            {!lockOtherFields && (<>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={flipAmountForSelected}
                                        onChange={(e) => setFlipAmountForSelected(e.target.checked)}
                                        disabled={busy}
                                        size="small"
                                        sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
                                    />
                                }
                                label={t('FlipAmountForSelected') || 'Flip amount (positive ↔ negative) for selected'}
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.825rem', lineHeight: 1.2 } }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={flipCardsForSelected}
                                        onChange={(e) => setFlipCardsForSelected(e.target.checked)}
                                        disabled={busy}
                                        size="small"
                                        sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
                                    />
                                }
                                label={t('FlipCardsForSelected') || 'Swap debit/credit for selected'}
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.825rem', lineHeight: 1.2 } }}
                            />
                            {supportsReplaceDescriptionOption && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={replaceDescriptionForSelected}
                                            onChange={(e) => setReplaceDescriptionForSelected(e.target.checked)}
                                            disabled={busy}
                                            size="small"
                                            sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
                                        />
                                    }
                                    label={t('ReplaceDescriptionWithAccountName') || 'Replace description with account name'}
                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.825rem', lineHeight: 1.2 } }}
                                />
                            )}
                            </>)}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={alignRegistrationMonthToDocumentDate}
                                        onChange={(e) => setAlignRegistrationMonthToDocumentDate(e.target.checked)}
                                        disabled={busy}
                                        size="small"
                                        sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
                                    />
                                }
                                label={t('AlignRegistrationMonthToDocumentDate')}
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.825rem', lineHeight: 1.2 } }}
                            />
                        </Box>
                    )}

                    {/* ───── Form ───── */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                        {/* Row 1 – Reporting month & Register month */}
                        <div className={shellStyles.formGrid2}>
                            <div>
                                <div className={modalStyles.fieldLabel}>{t('MonthReportDate')}</div>
                                <div
                                    className={`${shellStyles.dateField}${dateError ? ` ${shellStyles.dateFieldError}` : ''}`}
                                >
                                    <CustomMonthDatePicker
                                        key={reportingMonthDatePickerKey}
                                        date={values.reportingMonthDate}
                                        onDateChange={(date) => {
                                            if (date) handleReportingMonthDateChange(date);
                                        }}
                                        height="40px"
                                    />
                                </div>
                            </div>

                            <div>
                                <div className={modalStyles.fieldLabel}>{t('MonthRegisterDate')}</div>
                                <div className={shellStyles.dateField}>
                                    <CustomMonthDatePicker
                                        date={alignRegistrationMonthToDocumentDate ? null : values.registrationMonthDate}
                                        onDateChange={d => d && handleField('registrationMonthDate', d.toISOString())}
                                        height="40px"
                                        disabled={alignRegistrationMonthToDocumentDate}
                                    />
                                </div>
                            </div>
                        </div>



                        <div>
                        {/* Row 2 – Sort Code & VAT */}
                        <div className={shellStyles.formGrid2}>
                            {/* Sort Code */}
                            <div style={lockedFieldStyle}>
                                <div className={modalStyles.fieldLabel}>{t('Code ID')}</div>
                                <AutocompleteSelectFilter
                                    options={sortCodeSelectOptions}
                                    selectedValues={
                                        values.sortCodeId
                                            ? (sortCodeSelectOptions.find((o) => o.value === values.sortCodeId) || {
                                                value: values.sortCodeId,
                                                label: String(values.sortCodeId),
                                            })
                                            : null
                                    }
                                    onChange={(_, nv) => handleField('sortCodeId', nv ? nv.value : '')}
                                    placeholder={t('Code ID')}
                                    isMultiple={false}
                                    direction={dir}
                                    searchable
                                    clearable
                                    portalMenu
                                />
                            </div>

                            {/* VAT */}
                            <div style={lockOtherFields && !vatEditableWhenLocked ? lockedFieldStyle : undefined}>
                                <div className={modalStyles.fieldLabel}>{t('VATPercentage')}</div>
                                <AutocompleteSelectFilter
                                    options={vatSelectOptions}
                                    selectedValues={
                                        values.vat === '' || values.vat == null
                                            ? { value: '', label: t('NoChange') }
                                            : (vatSelectOptions.find((o) => String(o.value) === String(values.vat)) || {
                                                value: values.vat,
                                                label: String(values.vat),
                                            })
                                    }
                                    onChange={(_, nv) => handleField('vat', nv ? nv.value : '')}
                                    placeholder={t('VATPercentage')}
                                    isMultiple={false}
                                    direction={dir}
                                    searchable
                                    clearable
                                    portalMenu
                                />
                            </div>
                        </div>
                        <div style={lockedFieldStyle}>

                        {/* Row 3 – Account Card (for PaymentAcceptances) OR Credit/Debit (for Expenses/Incomes) */}
                        {type === 'PaymentAcceptances' ? (
                            // PaymentAcceptance: Single account card + Amount
                            <>
                                <div className={shellStyles.formGrid2}>
                                    {values.sortCodeId !== '3' && (
                                        <div>
                                            <div className={modalStyles.fieldLabel}>{t('SupplierCustomerName')}</div>
                                            <AutocompleteSelectFilter
                                                options={accountSelectOptions}
                                                selectedValues={findAccountOption(values.accountCodeId)}
                                                onChange={(_, nv) => handleField('accountCodeId', nv ? (nv.value ?? nv.cardId) : '')}
                                                placeholder={t('SupplierCustomerName')}
                                                isMultiple={false}
                                                direction={dir}
                                                searchable
                                                clearable
                                                portalMenu
                                            />
                                        </div>
                                    )}

                                    {/* Amount */}
                                    <div className={values.sortCodeId === '3' ? shellStyles.fieldFull : undefined}>
                                        <div className={modalStyles.fieldLabel} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            {t('Amount')}
                                            <Tooltip title={t('MakeAmountOpposite') || 'Make amount opposite (positive ↔ negative)'}>
                                                <IconButton
                                                    size="small"
                                                    sx={{ p: 0.5 }}
                                                    onClick={handleFlipAmount}
                                                    disabled={busy}
                                                >
                                                    <SwapHorizIcon sx={{ fontSize: 18, color: '#304FFF' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                        <TextField
                                            fullWidth
                                            type="text"
                                            inputMode="decimal"
                                            placeholder={t('Amount')}
                                            value={amountInput}
                                            onFocus={() => {
                                                setAmountInput(
                                                    values.amount !== undefined && values.amount !== null
                                                        ? String(Number(values.amount))
                                                        : ''
                                                );
                                            }}
                                            onBlur={(e) => handleAmountBlurOrEnter(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAmountBlurOrEnter(e.target.value);
                                                }
                                            }}
                                            onChange={(e) => {
                                                lastAmountEditRef.current = 'gross';
                                                const v = e.target.value;
                                                setAmountInput(v);
                                                const trimmed = String(v).trim();
                                                if (trimmed === '' || /^-?\d+(\.\d+)?$/.test(trimmed)) {
                                                    handleChangeAmount(v);
                                                }
                                            }}
                                            variant="standard"
                                            className={fieldStyles.styleInput}
                                            size="small"
                                            InputProps={comboInputProps}
                                            inputProps={{ ...inputStyle, inputMode: 'decimal' }}
                                        />
                                    </div>
                                </div>

                                {/* Debit and Credit Accounts for PaymentAcceptances */}
                                <div className={shellStyles.formGrid2}>
                                    <div>
                                        <div className={modalStyles.fieldLabel} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            {t('Debit Account')}
                                            <Tooltip title={t('SwapAccounts') || 'Swap Debit and Credit Accounts'}>
                                                <IconButton
                                                    size="small"
                                                    sx={{ p: 0.5 }}
                                                    onClick={handleSwapAccounts}
                                                    disabled={busy}
                                                >
                                                        <SwapHorizIcon sx={{ fontSize: 18, color: '#304FFF' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                        <AutocompleteSelectFilter
                                            options={accountSelectOptions}
                                            selectedValues={findAccountOption(values.debitAccount)}
                                            onChange={(_, nv) => handleField('debitAccount', nv ? (nv.value ?? nv.cardId) : '')}
                                            placeholder={t('Debit Account')}
                                            isMultiple={false}
                                            direction={dir}
                                            searchable
                                            clearable
                                            portalMenu
                                        />
                                    </div>

                                    <div>
                                        <div className={modalStyles.fieldLabel}>{t('Credit Account')}</div>
                                        <AutocompleteSelectFilter
                                            options={accountSelectOptions}
                                            selectedValues={findAccountOption(values.creditAccount)}
                                            onChange={(_, nv) => handleField('creditAccount', nv ? (nv.value ?? nv.cardId) : '')}
                                            placeholder={t('Credit Account')}
                                            isMultiple={false}
                                            direction={dir}
                                            searchable
                                            clearable
                                            portalMenu
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            // Expenses/Incomes: Debit and Credit accounts
                            <>
                                <div
                                    className={shellStyles.formGrid2}
                                    style={duplicateMode ? { alignItems: 'start' } : undefined}
                                >
                                    {/* Debit */}
                                    <div style={duplicateMode ? { gridColumn: 1, gridRow: 1 } : undefined}>
                                        <div className={modalStyles.fieldLabel}>{t('Debit Account')}</div>
                                        <AutocompleteSelectFilter
                                            options={accountSelectOptions}
                                            selectedValues={findAccountOption(values.debitAccount)}
                                            onChange={(_, nv) => handleField('debitAccount', nv ? (nv.value ?? nv.cardId) : '')}
                                            placeholder={t('Debit Account')}
                                            isMultiple={false}
                                            direction={dir}
                                            searchable
                                            clearable
                                            portalMenu
                                        />
                                    </div>

                                    {/* Amount */}
                                    <div style={duplicateMode ? { gridColumn: 2, gridRow: 1 } : undefined}>
                                        <div className={modalStyles.fieldLabel} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            {t('Amount')}
                                            <Tooltip title={t('MakeAmountOpposite') || 'Make amount opposite (positive ↔ negative)'}>
                                                <IconButton
                                                    size="small"
                                                    sx={{ p: 0.5 }}
                                                    onClick={handleFlipAmount}
                                                    disabled={busy}
                                                >
                                                    <SwapHorizIcon sx={{ fontSize: 18, color: '#304FFF' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                        <TextField
                                            fullWidth
                                            type="text"
                                            inputMode="decimal"
                                            placeholder={t('Amount')}
                                            value={amountInput}
                                            onFocus={() => {
                                                setAmountInput(
                                                    values.amount !== undefined && values.amount !== null
                                                        ? String(Number(values.amount))
                                                        : ''
                                                );
                                            }}
                                            onBlur={(e) => handleAmountBlurOrEnter(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAmountBlurOrEnter(e.target.value);
                                                }
                                            }}
                                            onChange={(e) => {
                                                lastAmountEditRef.current = 'gross';
                                                const v = e.target.value;
                                                setAmountInput(v);
                                                const trimmed = String(v).trim();
                                                if (trimmed === '' || /^-?\d+(\.\d+)?$/.test(trimmed)) {
                                                    handleChangeAmount(v);
                                                }
                                            }}
                                            variant="standard"
                                            className={fieldStyles.styleInput}
                                            size="small"
                                            InputProps={comboInputProps}
                                            inputProps={{ ...inputStyle, inputMode: 'decimal' }}
                                        />
                                    </div>
                                    <div
                                        className={!showProjectAssignRow && !duplicateMode ? shellStyles.fieldFull : undefined}
                                        style={duplicateMode ? { gridColumn: 1, gridRow: 2 } : undefined}
                                    >
                                        <div className={modalStyles.fieldLabel} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            {t('Credit Account')}
                                            <Tooltip title={t('SwapAccounts') || 'Swap Debit and Credit Accounts'}>
                                                <IconButton
                                                    size="small"
                                                    sx={{ p: 0.5 }}
                                                    onClick={handleSwapAccounts}
                                                    disabled={busy}
                                                >
                                                    <SwapVertIcon sx={{ fontSize: 18, color: '#304FFF' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                        <AutocompleteSelectFilter
                                            options={accountSelectOptions}
                                            selectedValues={findAccountOption(values.creditAccount)}
                                            onChange={(_, nv) => handleField('creditAccount', nv ? (nv.value ?? nv.cardId) : '')}
                                            placeholder={t('Credit Account')}
                                            isMultiple={false}
                                            direction={dir}
                                            searchable
                                            clearable
                                            portalMenu
                                        />
                                    </div>

                                    {showProjectAssignRow && (
                                        <div style={duplicateMode ? { gridColumn: 1, gridRow: 3 } : undefined}>
                                            <div className={modalStyles.fieldLabel}>{t('Projects')}</div>
                                            <AutocompleteSelectFilter
                                                options={projectAutocompleteOptions}
                                                selectedValues={selectedProjectOption}
                                                onChange={handleProjectAutocompleteChange}
                                                placeholder={t('Projects')}
                                                isMultiple={false}
                                                direction={dir}
                                                searchable
                                                clearable
                                                portalMenu
                                                isDisabled={busy || projectCreateLoading}
                                            />
                                        </div>
                                    )}

                            {/* VAT Fields (duplicate mode only) — stacked under Amount */}
                            {duplicateMode && (
                                <>
                                        <div style={{ gridColumn: 2, gridRow: 2 }}>
                                            <div className={modalStyles.fieldLabel}>{t('VAT')} {currentVat * 100}%</div>
                                            <TextField
                                                fullWidth
                                                placeholder={t('VAT')}
                                                value={beautifyNumber(values.amount ? Number(values.amount) - beforeVatAmount : 0)}
                                                disabled
                                                variant="standard"
                                                InputProps={comboInputProps}
                                                className={fieldStyles.styleInput}
                                                size="small"
                                                inputProps={inputStyle}
                                            />
                                        </div>
                                        <div style={{ gridColumn: 2, gridRow: 3 }}>
                                            <div className={modalStyles.fieldLabel}>{t('AmountBeforeVAT')}</div>
                                            <TextField
                                                fullWidth
                                                type="text"
                                                inputMode="decimal"
                                                placeholder={t('AmountBeforeVAT')}
                                                value={netAmountInput}
                                                onFocus={() => {
                                                    netFocusedRef.current = true;
                                                    setNetAmountInput(
                                                        netAmountInput !== '' && !isNaN(Number(netAmountInput))
                                                            ? String(Number(netAmountInput))
                                                            : ''
                                                    );
                                                }}
                                                onBlur={(e) => handleNetAmountBlurOrEnter(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleNetAmountBlurOrEnter(e.target.value);
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    const trimmed = String(v).trim();
                                                    if (trimmed === '' || /^-?\d+(\.\d+)?$/.test(trimmed)) {
                                                        handleChangeNetAmount(v);
                                                    } else {
                                                        lastAmountEditRef.current = 'net';
                                                        setNetAmountInput(v);
                                                    }
                                                }}
                                                variant="standard"
                                                className={fieldStyles.styleInput}
                                                size="small"
                                                InputProps={comboInputProps}
                                                inputProps={{ ...inputStyle, inputMode: 'decimal' }}
                                            />
                                        </div>
                                </>
                            )}
                                </div>
                            </>
                        )}

                        {/* Row 4 – Description (duplicate mode, non-PaymentAcceptances) */}
                        {duplicateMode && type !== 'PaymentAcceptances' && (
                            <div className={shellStyles.formGrid2}>
                                <div className={shellStyles.fieldFull}>
                                    <div className={modalStyles.fieldLabel}>{t('Description')}</div>
                                    <TextField
                                        fullWidth
                                        placeholder={t('enterDescription')}
                                        value={values.description}
                                        onChange={e => handleField('description', e.target.value)}
                                        variant="standard"
                                        InputProps={comboInputProps}
                                            className={fieldStyles.styleInput}
                                            size="small"
                                        inputProps={inputStyle}
                                    />
                                </div>
                            </div>
                        )}

                                        {/* Row 4 – Description & Withholding Tax (PaymentAcceptances only) */}
                        {type === 'PaymentAcceptances' && (
                            <div className={shellStyles.formGrid2}>
                                <div>
                                    <div className={modalStyles.fieldLabel}>{t('enterDescription')}</div>
                                    <TextField
                                        fullWidth
                                        placeholder={t('enterDescription')}
                                        value={values.description}
                                        onChange={e => handleField('description', e.target.value)}
                                        variant="standard"
                                        InputProps={comboInputProps}
                                            className={fieldStyles.styleInput}
                                            size="small"
                                        inputProps={inputStyle}
                                    />
                                </div>

                                <div>
                                    <div className={modalStyles.fieldLabel}>{t('WithholdingTax')}</div>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        placeholder={t('WithholdingTax')}
                                        value={values.withholdingTax}
                                        onChange={e => handleField('withholdingTax', e.target.value)}
                                        variant="standard"
                                        InputProps={comboInputProps}
                                            className={fieldStyles.styleInput}
                                            size="small"
                                        inputProps={inputStyle}
                                    />
                                </div>
                            </div>
                        )}

                        {
                            duplicateMode && type !== 'PaymentAcceptances' && (
                                <div className={shellStyles.formGrid2}>
                                    <div>
                                        <div className={modalStyles.fieldLabel}>{t('Currency')}</div>
                                        <AutocompleteSelectFilter
                                            options={currencySelectOptions}
                                            selectedValues={
                                                values.foreignCurrencyCode === '' || values.foreignCurrencyCode == null
                                                    ? { value: '', label: t('NoChange') }
                                                    : (currencySelectOptions.find(
                                                        (o) => String(o.value) === String(values.foreignCurrencyCode)
                                                    ) || {
                                                        value: values.foreignCurrencyCode,
                                                        label: String(values.foreignCurrencyCode),
                                                    })
                                            }
                                            onChange={(_, nv) => {
                                                const v = nv ? nv.value : '';
                                                if (v === '__CLEAR__') {
                                                    handleField('foreignCurrencyCode', '');
                                                    handleField('foreignCurrencyAmount', '');
                                                    handleField('amount', '');
                                                    setCurrencyAmountInput('');
                                                    setAmountInput('');
                                                } else {
                                                    handleField('foreignCurrencyCode', v);
                                                }
                                            }}
                                            placeholder={t('Currency')}
                                            isMultiple={false}
                                            direction={dir}
                                            searchable
                                            clearable
                                            portalMenu
                                        />
                                    </div>

                                    <div>
                                        <div className={modalStyles.fieldLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            {t('ForeignCurrencyAmount')}
                                            {currencyWarn && (
                                                <span style={{ color: "red", fontSize: "0.85em" }}>
                                                    {t('ToCalculateAmountWithForeignCurrencyChooseDateAndCurrencyCode')}
                                                </span>
                                            )}
                                        </div>
                                        <TextField
                                            fullWidth
                                            type="text"
                                            inputMode="decimal"
                                            placeholder={t('ForeignCurrencyAmount')}
                                            value={currencyAmountInput}
                                            onFocus={() => {
                                                setCurrencyAmountInput(
                                                    values.foreignCurrencyAmount !== undefined && values.foreignCurrencyAmount !== null
                                                        ? String(Number(values.foreignCurrencyAmount))
                                                        : ''
                                                );
                                            }}
                                            onBlur={(e) => handleCurrencyAmountBlurOrEnter(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleCurrencyAmountBlurOrEnter(e.target.value);
                                                }
                                            }}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setCurrencyAmountInput(v);
                                                const trimmed = String(v).trim();
                                                if (trimmed === '' || /^-?\d+(\.\d+)?$/.test(trimmed)) {
                                                    handleChangeForeignCurrencyAmount(v);
                                                }
                                            }}
                                            variant="standard"
                                            className={fieldStyles.styleInput}
                                            size="small"
                                            InputProps={comboInputProps}
                                            inputProps={{ ...inputStyle, inputMode: 'decimal' }}
                                        />
                                    </div>
                                </div>
                            )
                        }

                        {/* Row 5 – Reference Fields (hide Reference2 for PaymentAcceptances) */}
                        <div className={shellStyles.formGrid2}>
                            <div className={type === 'PaymentAcceptances' ? shellStyles.fieldFull : undefined}>
                                <div className={modalStyles.fieldLabel}>{t('Reference')}</div>
                                <TextField
                                    fullWidth
                                    placeholder={t('Reference')}
                                    value={values.reference}
                                    onChange={e => handleField('reference', e.target.value)}
                                    variant="standard"
                                    InputProps={comboInputProps}
                                            className={fieldStyles.styleInput}
                                            size="small"
                                    inputProps={inputStyle}
                                />
                            </div>

                            {type !== 'PaymentAcceptances' && (
                                <div>
                                    <div className={modalStyles.fieldLabel}>{t('Reference2')}</div>
                                    <TextField
                                        fullWidth
                                        placeholder={t('Reference2')}
                                        value={values.reference2}
                                        onChange={e => handleField('reference2', e.target.value)}
                                        variant="standard"
                                        InputProps={comboInputProps}
                                            className={fieldStyles.styleInput}
                                            size="small"
                                        inputProps={inputStyle}
                                    />
                                </div>
                            )}
                        </div>

                        </div>
                        </div>

                    </div>

                    {/* ───── Saved Entries Table ───── */}
                    {savedEntries.length > 0 && (
                        <div className={modalStyles.tableWrap} style={{ marginTop: 16, marginBottom: 8, maxHeight: 400 }}>
                                <TableContainer dir={dir} component={Paper}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ 
                                                    fontWeight: 700, 
                                                    fontSize: 12,
                                                    backgroundColor: '#304FFF',
                                                    color: '#ffffff',
                                                    textAlign: 'center',
                                                    width: '50px',
                                                    py: 0.5,
                                                    px: 1
                                                }}>
                                                    {t('UploadNumber') || 'Upload #'}
                                                </TableCell>
                                                {type !== 'PaymentAcceptances' && (
                                                    <TableCell sx={{ 
                                                        fontWeight: 700, 
                                                        fontSize: 12,
                                                        backgroundColor: '#304FFF',
                                                        color: '#ffffff',
                                                        textAlign: 'left',
                                                        py: 0.5,
                                                        px: 1
                                                    }}>
                                                        {t('Code ID')}
                                                    </TableCell>
                                                )}
                                                {type === 'PaymentAcceptances' ? (
                                                    <TableCell sx={{ 
                                                        fontWeight: 700, 
                                                        fontSize: 12,
                                                        backgroundColor: '#304FFF',
                                                        color: '#ffffff',
                                                        textAlign: 'left',
                                                        py: 0.5,
                                                        px: 1
                                                    }}>
                                                        {t('SupplierCustomerName')}
                                                    </TableCell>
                                                ) : (
                                                    <>
                                                        <TableCell sx={{ 
                                                            fontWeight: 700, 
                                                            fontSize: 12,
                                                            backgroundColor: '#304FFF',
                                                            color: '#ffffff',
                                                            textAlign: 'left',
                                                            py: 0.5,
                                                            px: 1
                                                        }}>
                                                            {t('Debit Account')}
                                                        </TableCell>
                                                        <TableCell sx={{ 
                                                            fontWeight: 700, 
                                                            fontSize: 12,
                                                            backgroundColor: '#304FFF',
                                                            color: '#ffffff',
                                                            textAlign: 'left',
                                                            py: 0.5,
                                                            px: 1
                                                        }}>
                                                            {t('Credit Account')}
                                                        </TableCell>
                                                    </>
                                                )}
                                                <TableCell sx={{ 
                                                    fontWeight: 700, 
                                                    fontSize: 12,
                                                    backgroundColor: '#304FFF',
                                                    color: '#ffffff',
                                                    textAlign: 'left',
                                                    py: 0.5,
                                                    px: 1
                                                }}>
                                                    {t('Amount')}
                                                </TableCell>
                                                <TableCell sx={{ 
                                                    fontWeight: 700, 
                                                    fontSize: 12,
                                                    backgroundColor: '#304FFF',
                                                    color: '#ffffff',
                                                    textAlign: 'left',
                                                    py: 0.5,
                                                    px: 1
                                                }}>
                                                    {t('Reference')}
                                                </TableCell>
                                                {duplicateMode && (
                                                    <TableCell sx={{ 
                                                        fontWeight: 700, 
                                                        fontSize: 12,
                                                        backgroundColor: '#304FFF',
                                                        color: '#ffffff',
                                                        textAlign: 'left',
                                                        py: 0.5,
                                                        px: 1
                                                    }}>
                                                        {t('Description')}
                                                    </TableCell>
                                                )}
                                                <TableCell sx={{ 
                                                    fontWeight: 700, 
                                                    fontSize: 12,
                                                    backgroundColor: '#304FFF',
                                                    color: '#ffffff',
                                                    textAlign: 'left',
                                                    py: 0.5,
                                                    px: 1
                                                }}>
                                                    {t('MonthReportDate')}
                                                </TableCell>
                                                <TableCell sx={{ 
                                                    fontWeight: 700, 
                                                    fontSize: 12,
                                                    backgroundColor: '#304FFF',
                                                    color: '#ffffff',
                                                    textAlign: 'center',
                                                    width: '80px',
                                                    py: 0.5,
                                                    px: 1
                                                }}>
                                                    {t('Actions')}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {savedEntries
                                                .map((entry, originalIndex) => ({ entry, originalIndex }))
                                                .sort((a, b) => {
                                                    // Sort by reportingMonthDate first (earliest first)
                                                    const dateA = a.entry.reportingMonthDate ? new Date(a.entry.reportingMonthDate).getTime() : 0;
                                                    const dateB = b.entry.reportingMonthDate ? new Date(b.entry.reportingMonthDate).getTime() : 0;
                                                    if (dateA !== dateB) {
                                                        return dateA - dateB;
                                                    }
                                                    // If dates are equal, sort by amount (largest first)
                                                    const amountA = Number(a.entry.amount) || 0;
                                                    const amountB = Number(b.entry.amount) || 0;
                                                    return amountB - amountA;
                                                })
                                                .map(({ entry, originalIndex }, sortedIndex) => (
                                                    <TableRow
                                                        key={originalIndex}
                                                        hover
                                                        onClick={() => handleSavedEntryRowClick(entry, originalIndex)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                backgroundColor: '#f5f5f5'
                                                            },
                                                            '& td': {
                                                                fontSize: 12,
                                                                py: 0.5,
                                                                px: 1,
                                                                whiteSpace: 'nowrap'
                                                            }
                                                        }}
                                                    >
                                                        <TableCell sx={{ textAlign: 'center' }}>
                                                            {entry.uploadNumber ? (
                                                                entry.uploadNumber
                                                            ) : (
                                                                <Chip
                                                                    label={sortedIndex + 1}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: '#304FFF',
                                                                        color: '#ffffff',
                                                                        fontSize: '10px',
                                                                        height: '20px',
                                                                        fontWeight: 600
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        {type !== 'PaymentAcceptances' && (
                                                            <TableCell>{getSortCodeName(entry.sortCodeId)}</TableCell>
                                                        )}
                                                        {type === 'PaymentAcceptances' ? (
                                                            <TableCell>{getAccountCardName(entry.accountCodeId)}</TableCell>
                                                        ) : (
                                                            <>
                                                                <TableCell>{getAccountCardName(entry.debitAccount)}</TableCell>
                                                                <TableCell>{getAccountCardName(entry.creditAccount)}</TableCell>
                                                            </>
                                                        )}
                                                        <TableCell>{beautifyNumber(entry.amount) || '-'}</TableCell>
                                                        <TableCell>{entry.reference || '-'}</TableCell>
                                                        {duplicateMode && (
                                                            <TableCell>{entry.description || '-'}</TableCell>
                                                        )}
                                                        <TableCell>
                                                            {entry.reportingMonthDate 
                                                                ? new Date(entry.reportingMonthDate).toLocaleDateString()
                                                                : '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: 'center' }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteSavedEntry(originalIndex);
                                                                }}
                                                                sx={{ color: '#d32f2f' }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                        </div>
                    )}
                            </div>

                        </div>
                        </div>
                    </div>
                </Draggable>
            </div>

            <ProjectModal
                open={projectModalOpen}
                mode="create"
                direction={dir}
                formData={{ code: '', name: '' }}
                existingProjects={segmentationProjects}
                onClose={handleProjectModalClose}
                onSave={handleProjectModalSave}
                loading={projectCreateLoading}
            />
        </Portal>
    );
}
